import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import { InMemoryCache, from } from '@apollo/client/core';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import * as Sentry from "@sentry/angular-ivy";
import { Apollo, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist';
import { AmplifyService } from 'aws-amplify-angular';
import { ConflictPolicy, EvictionPolicy } from 'cardihab-fhir-cache/fhir-cache';
import { Ng2TelInputModule } from 'ng2-tel-input';
import { Environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { UIComponentsModule } from './components/uicomponents.module';
import { ConsentPage } from './pages/authentication/consent/consent.page';
import { ContactUsPage } from './pages/authentication/contact-us/contact-us.page';
import { HomePage } from './pages/authentication/home/home.page';
import { LoginPage } from './pages/authentication/login/login.page';
import { RegisterPage } from './pages/authentication/register/register.page';
import { SiteModalPage } from './pages/authentication/register/site-modal/site-modal';
import { VerifyLoginPage } from './pages/authentication/verify-login/verify-login.page';
import { VerifySignUpPage } from './pages/authentication/verify-sign-up/verify-sign-up.page';
import { PipesModule } from './pipes/pipes.module';
import { ContentService } from './services/content.service';
import { StrapiAuthInterceptor } from './services/strapi-auth.service';


@NgModule({
  declarations: [AppComponent, HomePage, ConsentPage, LoginPage, RegisterPage, VerifySignUpPage, VerifyLoginPage, SiteModalPage, ContactUsPage],
  imports: [
    BrowserModule,
    CommonModule,
    IonicModule.forRoot({
      swipeBackEnabled: false,
    }),
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    Ng2TelInputModule,
    PipesModule,
    UIComponentsModule,
    ApolloModule,
    HttpClientModule,
    BrowserAnimationsModule,
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    AmplifyService,
    HttpClientModule,
    ContentService,
    {
      provide: 'cacheConfig',
      useValue: {
        encryptionKey: 'thisisanencryptionkey',
        resources: {
          Device: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          MedicationRequest: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          MedicationStatement: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Goal: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Provenance: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          CarePlan: {
            pageSize: 100,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          EpisodeOfCare: {
            pageSize: 100,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Organization: {
            pageSize: 100,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Observation: {
            pageSize: 10000,
            cacheLimit: 200000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
        },
        fhirDetails: {
          fhirJWT: null,
          fhirUrl: Environment.HAPI_FHIR_URL,
        }
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StrapiAuthInterceptor,
      multi: true
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private apollo: Apollo,
    private httpLink: HttpLink,
  ) {
    this.createApollo();
  }

  async createApollo() {
    const uri = Environment.CONTENT_URL + '/graphql';

    const cache = new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            article(_: any, { args, toReference }: any) {
              if (args.id) {
                return toReference({
                  __typename: 'ArticleEntity',
                  id: args.id,
                });
              }
            },
          }
        }
      }
    });
    await persistCache({
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
    });
    this.apollo.create({
      link: from([this.httpLink.create({ uri })]),
      cache,
      connectToDevTools: true,
      defaultOptions: {
        query: {
          errorPolicy: 'ignore',
          fetchPolicy: 'cache-first',
        }
      }
    });
  }
}
